import { type ApolloError, type DocumentNode, gql, useLazyQuery, useQuery } from '@apollo/client';
import { Grid } from '@mui/material';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import type React from 'react';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import CollectionVerticalView from 'src/CollectionsPage/CollectionItems/CollectionVerticalView';
import useCollectionPageStyles from 'src/CollectionsPage/CollectionPageStyles';
import { DownloadManager, queueAndDownload } from 'src/download';
import { CollectionSimple } from 'src/dynamoQueries/collections';
import { buildDownloadLinksQ, buildSharedDownloadLinksQ } from 'src/dynamoQueries/download';
import { getCollectionItemGlobalIds } from 'src/helpers';
import { cleanPayload } from 'src/helpers/common';
import { useCustomToast } from 'src/hooks/menu';
import { onDownloadLinkErrorMsg } from 'src/messages/error';
import viewStateVar from 'src/store/viewState';
import CollectionItemsList, { type CollectionItem } from './CollectionItems/CollectionItemView/CollectionItemsList';

const perPage = 10;

interface ContentObject {
  id: string;
}

interface CollectionItemNode {
  contentObject: ContentObject;
}

interface CollectionItemEdge {
  cursor: string;
  node: CollectionItemNode;
}

interface PageInfo {
  endCursor: string;
  startCursor: string;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

interface CollectionItems {
  totalCount: number;
  pageInfo: PageInfo;
  edges: CollectionItemEdge[];
}

interface CollectionData {
  id: string;
  name: string;
  items: CollectionItems;
}

interface CollectionQueryResult {
  collection?: CollectionData;
  sharedCollection?: CollectionData;
}

interface DownloadLink {
  globalId: string;
  itemId: string;
  downloadPath: string;
  filename: string;
}

interface DownloadLinksQueryResult {
  downloadLinks?: DownloadLink[];
  sharedDownloadLinks?: DownloadLink[];
}

interface CollectionViewProps {
  shared: boolean;
  collectionId: string;
  downloadable: boolean;
}

const CollectionView: React.FC<CollectionViewProps> = ({ shared, collectionId, downloadable }) => {
  const classes = useCollectionPageStyles();
  const collectionkey: 'sharedCollection' | 'collection' = shared ? 'sharedCollection' : 'collection';
  const { toastr: cToastr } = useCustomToast();

  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  const buildQuery = (): DocumentNode => {
    const query = CollectionSimple(true);

    if (shared) {
      // perform the exact same query except we replace gql endpoint with sharedCollection
      query.query.sharedCollection = query.query.collection;
      // remove the original query
      delete query.query.collection;
    }
    return gql(jsonToGraphQLQuery(query, { pretty: true }));
  };

  const { data, loading, error, fetchMore } = useQuery<CollectionQueryResult>(buildQuery(), {
    variables: { id: collectionId, first: perPage },
  });

  const [getDownloadLinks] = useLazyQuery<DownloadLinksQueryResult>(
    shared ? buildSharedDownloadLinksQ() : buildDownloadLinksQ(),
  );

  const canFetchMore = (): boolean => {
    const { hasNextPage } = data?.[collectionkey]?.items?.pageInfo ?? {};
    if (hasNextPage) {
      return true;
    }
    return false;
  };

  const collectionName = shared ? data?.sharedCollection?.name : data?.collection?.name;

  const fetchMoreItems = (): void => {
    const endCursor = data?.[collectionkey]?.items?.pageInfo?.endCursor;
    fetchMore({ variables: { first: 5, after: endCursor } });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally only trigger when selectedItemId changes to fetch more items; data and fetchMoreItems are stable between renders
  useEffect(() => {
    if (selectedItemId && data) {
      const { endCursor, hasNextPage } = data[collectionkey]?.items?.pageInfo ?? {};
      const selectedNode = data[collectionkey]?.items?.edges?.find(
        ({ node }) => node.contentObject.id === selectedItemId,
      );

      if (selectedNode && selectedNode.cursor === endCursor && hasNextPage) {
        fetchMoreItems();
      }
    }
  }, [selectedItemId]);

  const handlePlayNext = (): void => {
    if (items) {
      const itemId = selectedItemId || items[0]?.contentObject?.id;
      const selectedIndex = items.findIndex(({ contentObject }) => contentObject.id === itemId);
      if (selectedIndex < items.length - 1) {
        const nextItem = items[selectedIndex + 1];
        setSelectedItemId(nextItem.contentObject.id);
      }
    }
  };

  const handleScrollToBottom = (): void => {
    canFetchMore() && fetchMoreItems();
  };

  const handleAssetDownload = async (): Promise<void> => {
    if (shared) {
      // Kebab menu should download a single asset in shared context
      const assetId = selectedItemId || data?.[collectionkey]?.items?.edges?.[0]?.node?.contentObject?.id;
      if (assetId) {
        viewStateVar({ ...viewStateVar(), sharedItemDownloadDialog: assetId });
      }
      return;
    }
    // Legacy non-shared immediate download
    getDownloadLinks({
      variables: {
        globalIds: getCollectionItemGlobalIds(data?.[collectionkey]?.items?.edges || []),
      },
    })
      .then(({ data: downloadData }) => {
        const { downloadLinks: regularDownloadLinks } = downloadData || {};
        const manager = new DownloadManager(6, true);
        regularDownloadLinks && queueAndDownload({ downloadLinks: regularDownloadLinks, manager, toastr: cToastr });
      })
      .catch((downloadError: ApolloError) => cToastr.error(onDownloadLinkErrorMsg(downloadError.message)));
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;

  const items: CollectionItem[] = cleanPayload(data?.[collectionkey]);

  return (
    <div className={classes.collectionPage}>
      <Helmet>
        <title>{collectionName}</title>
      </Helmet>

      <Grid container spacing={2} justifyContent="center" alignItems="flex-start">
        <Grid item xs={12} md={7}>
          <CollectionVerticalView
            shared={shared}
            asset={{ id: selectedItemId || items[0]?.contentObject?.id }}
            downloadable={downloadable}
            handleDownload={handleAssetDownload}
            onVideoEnded={handlePlayNext}
            autoplay={true}
          />
        </Grid>
        <Grid item xs={12} md={5}>
          <CollectionItemsList
            name={collectionName || ''}
            items={items}
            shared={shared}
            selectedItemId={selectedItemId || items[0]?.contentObject?.id}
            itemClickCallback={setSelectedItemId}
            handleScrollToBottom={handleScrollToBottom}
          />
        </Grid>
      </Grid>
    </div>
  );
};

export type { CollectionViewProps };
export default CollectionView;
